import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, GraduationCap } from "lucide-react";
import { api } from "../lib/api";

const LANGS = [
  { key: "English", label: "EN" },
  { key: "Français", label: "FR" },
  { key: "Kreyòl", label: "HT" },
  { key: "Español", label: "ES" },
];

// Floating AI Study Coach available across the whole Learning Center.
// Answers questions on any TAVEN course topic on demand via /api/ai/chat (tutor mode).
export const LearningCoach = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("English");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hi! I'm your TAVEN Study Coach. Ask me anything about e-commerce, selling, dropshipping, marketing, affiliates, branding, or any TAVEN course — I'm here to help you learn." },
  ]);
  const sessionRef = useRef(`coach-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionRef.current,
        message: text,
        mode: "tutor",
        course: "TAVEN Academy — general learning",
        language: lang,
      });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="study-coach-fab"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 bg-taven-gold text-black font-bold uppercase tracking-wider text-xs px-4 py-3 rounded-full shadow-2xl hover:bg-white transition-colors"
        >
          <Sparkles className="w-4 h-4" /> Study Coach
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-sm bg-white border border-black/10 shadow-2xl flex flex-col" data-testid="study-coach-panel" style={{ height: "min(560px, 80vh)" }}>
          <div className="flex items-center justify-between bg-taven-black text-white px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-taven-gold text-black flex items-center justify-center"><GraduationCap className="w-4 h-4" /></div>
              <div>
                <p className="text-sm font-black font-display leading-none">TAVEN Study Coach</p>
                <p className="text-[10px] text-white/50 uppercase tracking-widest mt-0.5">AI learning assistant</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="study-coach-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex items-center gap-1 px-3 py-2 border-b border-black/5">
            {LANGS.map((l) => (
              <button key={l.key} onClick={() => setLang(l.key)} data-testid={`coach-lang-${l.label}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${lang === l.key ? "bg-taven-gold text-black" : "text-black/40 hover:text-black"}`}>{l.label}</button>
            ))}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="study-coach-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm leading-relaxed px-3 py-2 ${m.role === "user" ? "bg-taven-black text-white" : "bg-taven-gray text-black"}`}>{m.content}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-taven-gray text-black/50 text-sm px-3 py-2">Thinking…</div></div>}
          </div>

          <div className="flex items-center gap-2 p-3 border-t border-black/5">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="study-coach-input"
              placeholder="Ask about any course topic…"
              className="flex-1 bg-taven-gray px-3 py-2.5 text-sm outline-none"
            />
            <button onClick={send} disabled={busy} data-testid="study-coach-send" className="bg-taven-gold text-black p-2.5 hover:bg-taven-black hover:text-taven-gold transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LearningCoach;
